import { E_CARD_SIZE_ID, Variant } from '@moonpig/web-shared-products'
import { Breadcrumb } from '../../types'

export const getPageSchema = ({
  id,
  title,
  description,
  image,
  masterVariantKey,
  variants,
  breadcrumbsItems,
  hostUrl,
  canonicalUrl,
}: {
  id: string
  title: string
  description: string
  image: string | undefined
  masterVariantKey: string
  variants: Variant[]
  breadcrumbsItems: Breadcrumb[]
  hostUrl: string
  canonicalUrl: string
}) => {
  const productGroup = {
    '@type': 'ProductGroup',
    '@id': `${canonicalUrl}#productgroup`,
    productGroupID: id,
    url: canonicalUrl,
    name: title,
    image,
    description,
    variesBy: ['https://schema.org/size'],
    hasVariant: variants
      .filter(variant => variant.key !== E_CARD_SIZE_ID)
      .map(({ title: size, key, price, sku, inStock }) => {
        const isMasterVariant = key === masterVariantKey
        const variantUrl = isMasterVariant
          ? canonicalUrl
          : `${canonicalUrl}?size=${key}`

        return {
          '@type': 'Product',
          '@id': variantUrl,
          url: variantUrl,
          name: isMasterVariant ? title : `${title} – ${size}`,
          sku,
          size,
          image,
          offers: {
            '@type': 'Offer',
            url: variantUrl,
            priceCurrency: price.currencyCode,
            price: price.centAmount / 10 ** price.fractionDigits,
            availability: inStock
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
            itemCondition: 'https://schema.org/NewCondition',
          },
        }
      }),
  }

  const eCard = variants
    .filter(v => v.key === E_CARD_SIZE_ID)
    .map(({ title: variantTitle, price, sku, inStock }) => ({
      '@type': 'Product',
      '@id': `${canonicalUrl}?size=${E_CARD_SIZE_ID}`,
      name: `${title} – ${variantTitle}`,
      sku,
      description,
      image,
      offers: {
        '@type': 'Offer',
        sku,
        priceCurrency: price.currencyCode,
        price: price.centAmount / 10 ** price.fractionDigits,
        availability: inStock
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      },
    }))[0]

  const breadcrumbs = {
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbsItems.map(({ url, label }, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: label,
      item: url.startsWith('/') ? `${hostUrl}${url}` : url,
    })),
  }

  const graph = [productGroup, eCard, breadcrumbs].filter(Boolean)
  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  }
}
